import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { cn } from "../internal/cn.js";

/**
 * Compact ghost icon control for dense surfaces (inputs, captions). Uses
 * the design-system focus ring. Distinct from `<Button>` — that's a full
 * CTA. Use `<InlineIconButton>` for clear-buttons inside inputs, copy
 * affordances next to inline values, etc.
 */
const inlineIconButtonVariants = cva("cw-v2-inline-icon-button", {
  variants: {
    size: {
      xs: "cw-v2-inline-icon-button--xs",
      sm: "cw-v2-inline-icon-button--sm",
      md: "cw-v2-inline-icon-button--md",
    },
  },
  defaultVariants: {
    size: "sm",
  },
});

export interface InlineIconButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children">,
    VariantProps<typeof inlineIconButtonVariants> {
  "aria-label": string;
  icon: React.ReactNode;
  /**
   * Optional visual variant. The cakewalk-ui dev branch (the source
   * of the lifted owner / team organisms) accepts a `variant` prop on
   * its inline-icon-button (e.g. `variant="ghost"`). The v2 surface
   * has only one visual style, so this prop is accepted but applied
   * as a className modifier (`cw-v2-inline-icon-button--<variant>`)
   * for any consumer styling that targets the variant string.
   */
  variant?: "ghost" | "default" | "subtle" | "destructive";
}

export const InlineIconButton = React.forwardRef<HTMLButtonElement, InlineIconButtonProps>(
  function InlineIconButton(
    { "aria-label": ariaLabel, className, icon, size, title, type = "button", variant, ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        aria-label={ariaLabel}
        className={cn(
          inlineIconButtonVariants({ size }),
          variant ? `cw-v2-inline-icon-button--${variant}` : undefined,
          className,
        )}
        title={title ?? ariaLabel}
        type={type}
        {...props}
      >
        <span className="cw-v2-inline-icon-button__icon">{icon}</span>
      </button>
    );
  },
);
InlineIconButton.displayName = "InlineIconButton";
